import { FormattedEvent } from '@eventbrite/consumer-search.event-renderer';
import { getLastRecentSearchTerms, getLastRecentViewsList } from '../utils/utils';
import { useQuery } from '@tanstack/react-query';

export const useRecommendations = ({ enabled }: { enabled: boolean }) => {
    return useQuery({
        queryKey: ['for-you-recommendations'],
        queryFn: fetchRecommendations,
        staleTime: 10 * 60 * 1000, // 10 minutes, means data is considered fresh for 10 minutes
        cacheTime: 30 * 60 * 1000, // 30 minutes, means data is considered stale for 30 minutes
        retry: false,
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        refetchOnMount: true,
        enabled,
    });
};

async function fetchRecommendations(): Promise<Bucket[]> {
    const recentSearches = getLastRecentSearchTerms(10);
    const recentViews = getLastRecentViewsList(10);
    const timeoutMs = 9000;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
        const response = await fetch(`/home/api/search/recommendations/`, {
            method: 'POST',
            body: JSON.stringify({ recentSearches, recentViews }),
            headers: { 'Content-Type': 'application/json' },
            signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
            console.error('Failed to fetch recommendations:', response.statusText);
            throw new Error('Failed to fetch recommendations');
        }
        const data = await response.json();
        return data.buckets as Bucket[];
    } catch (error) {
        clearTimeout(timeoutId);
        if (error instanceof Error && error.name === 'AbortError') {
            throw new Error(`Recommendations request timed out after ${timeoutMs}ms`);
        }
        throw error;
    }
}

interface Bucket {
    events: FormattedEvent[];
    name: string;
}
